import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../services/notificationService';
import { RESPONSE_TYPES } from '../constants/notificationConstants';

// Hook for managing notifications
export const useNotifications = () => {
  const { user, isAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load notifications based on user role
  const loadNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      let notificationData;
      if (isAdmin()) {
        // UPDATED: Admin users now see BOTH sent and received notifications
        // For the main notifications hook, show received notifications (inbox view)
        // The history hook will handle sent notifications separately
        notificationData = await notificationService.getUserNotifications(user.uid);
      } else {
        // Department users see received notifications
        notificationData = await notificationService.getUserNotifications(user.uid);
      }

      setNotifications(notificationData);

      // Update unread count for all users (both admin and department)
      const count = notificationData.filter(n => !n.isRead).length;
      setUnreadCount(count);
      
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  // Send notification (admin only)
  const sendNotification = async (notificationData) => {
    try {
      setError(null);
      const result = await notificationService.sendNotification({
        ...notificationData,
        senderId: user.uid
      });
      
      // Reload notifications to show any new received ones
      await loadNotifications();
      
      return result;
    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err.message);
      throw err;
    }
  };

  // Respond to notification (all users)
  const respondToNotification = async (notificationId, response) => {
    try {
      setError(null);
      await notificationService.respondToNotification(notificationId, user.uid, response);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { 
                ...notification, 
                userResponse: { 
                  response, 
                  respondedAt: new Date() 
                } 
              }
            : notification
        )
      );
      
      return true;
    } catch (err) {
      console.error('Error responding to notification:', err);
      setError(err.message);
      throw err;
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId, user.uid);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true }
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  // Load notifications on mount and user change
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Get unread count
  const getUnreadCount = useCallback(async () => {
    if (!user) return 0;
    
    try {
      const count = await notificationService.getUnreadCount(user.uid);
      setUnreadCount(count);
      return count;
    } catch (err) {
      console.error('Error getting unread count:', err);
      return 0;
    }
  }, [user]);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    sendNotification,
    respondToNotification,
    markAsRead,
    loadNotifications,
    getUnreadCount,
    clearError: () => setError(null)
  };
};

// Hook for notification history (admin specific)
export const useNotificationHistory = () => {
  const { user, isAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);

  // Load notification history
  const loadHistory = useCallback(async () => {
    if (!user || !isAdmin()) return;

    try {
      setLoading(true);
      setError(null);
      
      const [historyData, statsData] = await Promise.all([
        notificationService.getNotificationHistory(user.uid),
        notificationService.getNotificationStatistics(user.uid)
      ]);
      
      setNotifications(historyData);
      setStatistics(statsData);
    } catch (err) {
      console.error('Error loading notification history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  // Get detailed status for a specific notification
  const getNotificationStatus = useCallback(async (notificationId) => {
    if (!user || !isAdmin()) return null;

    try {
      return await notificationService.getNotificationDetailedStatus(notificationId);
    } catch (err) {
      console.error('Error getting notification status:', err);
      throw err;
    }
  }, [user, isAdmin]);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return {
    notifications,
    statistics,
    loading,
    error,
    loadHistory,
    getNotificationStatus,
    clearError: () => setError(null)
  };
};

// UPDATED: Hook for sending notifications (admin specific)
export const useSendNotification = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const uploadAttachment = async (file, notificationId) => {
    try {
      const attachment = await notificationService.uploadAttachment(file, notificationId);
      return attachment;
    } catch (err) {
      console.error('Error uploading attachment:', err);
      throw err;
    }
  };

  const sendNotification = async (notificationData) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await notificationService.sendNotification(notificationData);
      return result;
    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // NEW: Add this method for updating notification with attachments
  const updateNotificationAttachments = async (notificationId, attachments) => {
    try {
      const result = await notificationService.updateNotificationAttachments(notificationId, attachments);
      return result;
    } catch (err) {
      console.error('Error updating notification attachments:', err);
      throw err;
    }
  };

  return {
    loading,
    error,
    sendNotification,
    uploadAttachment,
    updateNotificationAttachments, // NEW: Add this
    clearError: () => setError(null)
  };
};

// Hook for notification status tracking (admin specific)
export const useNotificationStatus = (notificationId) => {
  const { user, isAdmin } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    if (!user || !isAdmin() || !notificationId) return;

    try {
      setLoading(true);
      setError(null);
      
      const statusData = await notificationService.getNotificationDetailedStatus(notificationId);
      setStatus(statusData);
    } catch (err) {
      console.error('Error loading notification status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, notificationId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  return {
    status,
    loading,
    error,
    reload: loadStatus,
    clearError: () => setError(null)
  };
};